import * as React from 'react';

import { useQuery } from '@apollo/client';

import type { AvailableSkill } from 'bundles/personalization-settings/hooks/useSkillsEditor';
import PersonalizationSkillsCatalogQuery from 'bundles/personalization-settings/queries/PersonalizationSkillsCatalog.graphql';
import type {
  PersonalizationSkillsCatalogQuery as PersonalizationSkillsCatalogQueryType,
  PersonalizationSkillsCatalogQueryVariables,
} from 'bundles/personalization-settings/queries/__generated__/PersonalizationSkillsCatalog';

/** Debounce applied to the typed input before a search fires (ms). */
export const SEARCH_DEBOUNCE_MS = 300;

/** How many skills to load — both the default list and per search. */
export const CATALOG_LIMIT = 20;

/**
 * Sort for the default (no-input) list: the most-enrolled skills first, so the
 * learner sees a useful set of popular skills before typing.
 */
const POPULAR_FIRST: PersonalizationSkillsCatalogQueryVariables['skillSorts'] = ['COURSE_ENROLLMENT_DESC'];

/** Debounces a changing value: returns the latest value only after `delay` ms of quiet. */
const useDebouncedValue = <T>(value: T, delay: number): T => {
  const [debounced, setDebounced] = React.useState(value);
  React.useEffect(() => {
    const timeout = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timeout);
  }, [value, delay]);
  return debounced;
};

export type UseSkillsCatalogSearchResult = {
  /** Skills to offer in the editor: search matches when typing, else the popular default list. */
  availableSkills: AvailableSkill[];
  /** True while a request for the current (debounced) input is in flight. */
  loading: boolean;
};

/**
 * Loads the skills to offer in the "Desired skills" editor from the shared
 * Skills service — the same `Skills.querySkills` source the onboarding skill
 * picker uses. With input it searches by name; with no input it loads a default
 * list of the most popular skills. Filtering is the backend's job, so the whole
 * response is handed to the editor as-is.
 *
 * The typed input is debounced so we don't query on every keystroke.
 */
const useSkillsCatalogSearch = (input: string): UseSkillsCatalogSearchResult => {
  const searchTerm = useDebouncedValue(input.trim(), SEARCH_DEBOUNCE_MS);
  const hasTerm = searchTerm.length > 0;

  const { data, loading } = useQuery<PersonalizationSkillsCatalogQueryType, PersonalizationSkillsCatalogQueryVariables>(
    PersonalizationSkillsCatalogQuery,
    {
      variables: hasTerm
        ? { skillNames: [searchTerm], limit: CATALOG_LIMIT }
        : { skillSorts: POPULAR_FIRST, limit: CATALOG_LIMIT },
      context: { clientName: 'gatewayGql' },
      errorPolicy: 'all',
    }
  );

  const availableSkills = React.useMemo<AvailableSkill[]>(
    () =>
      (data?.Skills?.querySkills.elements ?? []).flatMap((skill) =>
        skill.id && skill.skillName ? [{ id: skill.id, name: skill.skillName }] : []
      ),
    [data]
  );

  return { availableSkills, loading };
};

export default useSkillsCatalogSearch;
